import { useState, useEffect } from "react";
import { YStack, XStack, Text, View } from "tamagui";
import { colors } from "@styles/colors";
import { Header, RADIUS } from "./Common";
import { Ic } from "./Icons";
import RadiusContentWrapper from "@components/layout/RadiusContentWrapper";

export default function StepMatching({ addr, onDone, onBack }: { addr: string; onDone: () => void; onBack: () => void }) {
  const [prog, setProg] = useState(0);

  useEffect(() => {
    window.dispatchEvent(new Event("scroll-to-top"));
  }, []);

  const steps = [
    { label: "確認地址定位", icon: "M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" },
    { label: "搜尋附近瓦斯行", icon: "M21 21l-4.35-4.35M10 17a7 7 0 110-14 7 7 0 010 14z" },
    { label: "比對服務範圍與評價", icon: "M9 12l2 2 4-4M21 12A9 9 0 113 12a9 9 0 0118 0z" },
  ];

  useEffect(() => {
    let p = 0;
    const tick = () => {
      p++;
      setProg(p);
      if (p < 3) setTimeout(tick, 300);
      else setTimeout(onDone, 200);
    };
    const timer = setTimeout(tick, 150);
    return () => clearTimeout(timer);
  }, [onDone]);

  return (
    <RadiusContentWrapper padding="0" space={0} background="transparent">
      <Header title="正在配對瓦斯行…" sub="步驟 2 / 3　請稍候片刻" step={2} total={3} onBack={onBack} />
      <YStack pt="$2" ai="center">
        <View w={108} h={108} br={54} mb="$8" position="relative">
          {[0, 1].map((i) => (
            <View
              key={i}
              position="absolute"
              t={0}
              l={0}
              r={0}
              b={0}
              m={i * 14}
              br={999}
              bg={i === 0 ? colors.primaryActive : colors.primaryActive}
              animation="quick"
              opacity={0.5}
              style={{
                animation: `pulse 1.2s ease-out ${i * 0.25}s infinite`,
              }}
            />
          ))}
          <YStack
            position="absolute"
            t={0}
            l={0}
            r={0}
            b={0}
            m={28}
            br={999}
            bg={colors.primary}
            ai="center"
            jc="center"
            style={{ boxShadow: `0 6px 22px ${colors.primary}55` }}>
            <Ic d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0zM12 7a3 3 0 100 6 3 3 0 000-6z" c="#fff" s={24} sw={2} />
          </YStack>
        </View>

        <Text fontSize={14} color={colors.gray} ta="center">
          正在分析{" "}
          <Text fontWeight="bold" color={colors.grayDark}>
            {addr || "你的地址"}
          </Text>
        </Text>

        <YStack bg={colors.light} br={RADIUS} py="$4" w="100%" maw={320}>
          {steps.map((s, i) => {
            const done = prog > i,
              active = prog === i;
            return (
              <XStack key={i} px="$5" py="$3" ai="center" gap="$3" opacity={prog >= i ? 1 : 0.35} animation="quick">
                <View
                  w={28}
                  h={28}
                  br={14}
                  bg={done ? colors.green : active ? colors.primaryActive : colors.bg}
                  ai="center"
                  jc="center"
                  flexShrink={0}
                  animation="quick">
                  {done ? (
                    <Ic d="M5 13l4 4L19 7" c="#fff" s={15} sw={2.5} />
                  ) : active ? (
                    <View
                      w={14}
                      h={14}
                      br={7}
                      borderWidth={2.5}
                      borderColor={colors.primary}
                      borderTopColor="transparent"
                      animation="quick"
                      style={{ animation: "spin 0.8s linear infinite" }}
                    />
                  ) : (
                    <Ic d={s.icon} c={colors.gray200} s={14} />
                  )}
                </View>
                <Text
                  fontSize={13}
                  fontWeight={done || active ? "700" : "400"}
                  color={done ? colors.homeGreen : active ? colors.grayDark : colors.gray200}
                  animation="quick">
                  {s.label}
                </Text>
                {active && (
                  <Text ml="auto" fontSize={10} color={colors.primary} fontWeight="600">
                    進行中
                  </Text>
                )}
              </XStack>
            );
          })}
        </YStack>
      </YStack>

      <style>{`
        @keyframes pulse { 0% { transform:scale(0.7); opacity:0.9; } 100% { transform:scale(1.45); opacity:0; } }
        @keyframes spin  { to { transform:rotate(360deg); } }
      `}</style>
    </RadiusContentWrapper>
  );
}
